import { basketball, cricketBall, flowerLotus, golfDriver, soccerBall, tennisBall, tennisRacket } from '@lucide/lab';
import { Activity, Bike, CircleDot, Dumbbell, Flame, Footprints, HandFist, HeartPulse, Mountain, PersonStanding, Salad, Stethoscope, Target, Trophy, Volleyball, Waves, createLucideIcon } from 'lucide-react';

export const SPORT_ICONS = {
  'tennis-racket': createLucideIcon('tennis-racket', tennisRacket),
  'tennis-ball': createLucideIcon('tennis-ball', tennisBall),
  'soccer-ball': createLucideIcon('soccer-ball', soccerBall),
  basketball: createLucideIcon('basketball', basketball),
  volleyball: Volleyball,
  'flower-lotus': createLucideIcon('flower-lotus', flowerLotus),
  'golf-driver': createLucideIcon('golf-driver', golfDriver),
  'cricket-ball': createLucideIcon('cricket-ball', cricketBall),
  'hand-fist': HandFist,
  stethoscope: Stethoscope,
  salad: Salad,
  dumbbell: Dumbbell,
  activity: Activity,
  bike: Bike,
  'circle-dot': CircleDot,
  flame: Flame,
  footprints: Footprints,
  'heart-pulse': HeartPulse,
  mountain: Mountain,
  'person-standing': PersonStanding,
  target: Target,
  trophy: Trophy,
  waves: Waves,
};

export type SportIconKey = keyof typeof SPORT_ICONS;

export function isSportIconKey(icon?: string | null): icon is SportIconKey {
  return !!icon && Object.hasOwn(SPORT_ICONS, icon);
}

// Most tenants never pick an icon and name sports freely (hot-yoga,
// reformer-pilates), so slug words map to a glyph when nothing is set.
const SLUG_ICON_KEYWORDS: Array<[keywords: string[], icon: string]> = [
  [['football', 'soccer', 'futsal'], 'soccer-ball'],
  [['basketball'], 'basketball'],
  [['volleyball'], 'volleyball'],
  [['tennis'], 'tennis-racket'],
  [['badminton', 'squash', 'pickleball'], 'tennis-racket'],
  [['yoga'], 'flower-lotus'],
  [['pilates', 'reformer', 'lagree', 'fitness', 'gym', 'crossfit', 'strength'], 'dumbbell'],
  [['golf'], 'golf-driver'],
  [['cricket'], 'cricket-ball'],
  [['boxing', 'kickboxing', 'mma', 'karate', 'taekwondo', 'jiujitsu'], 'hand-fist'],
  [['running', 'run', 'athletics'], 'footprints'],
  [['swimming', 'swim', 'aqua', 'pool'], 'waves'],
  [['cycling', 'spin', 'bike'], 'bike'],
  [['climbing', 'bouldering'], 'mountain'],
  [['physiotherapy', 'physio'], 'stethoscope'],
  [['nutrition'], 'salad'],
];

export function sportIconKeyForSlug(slug: string): SportIconKey | null {
  const words = slug.toLowerCase().split(/[-_\s]+/);
  for (const [keywords, icon] of SLUG_ICON_KEYWORDS) {
    if (isSportIconKey(icon) && keywords.some((keyword) => words.includes(keyword))) {
      return icon;
    }
  }
  return null;
}

/** Chosen icon (home override, then the sport's own), then the slug match. */
export function resolveSportIconKey(slug: string, ...chosen: Array<string | null | undefined>): SportIconKey | null {
  return chosen.find(isSportIconKey) ?? sportIconKeyForSlug(slug);
}

export function getSportIcon(icon?: string | null) {
  return isSportIconKey(icon) ? SPORT_ICONS[icon] : Dumbbell;
}
